import {api} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {FieldObserver} from "@/shared/ui/fieldObserver";
import * as Constants from "@/shared/constants";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {convertColorIntToHex, copyOfObject, serializeObjectToArray} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {DriveLogCategory, ErrorResponse} from "@/shared/timr";

export class DriveLogCategoriesAdministrationPage {
	private readonly driveLogCategoriesTable: DriveLogCategoriesTable;
	private readonly driveLogCategoriesDialogHandler: DriveLogCategoriesDialogHandler;

	constructor() {
		this.driveLogCategoriesTable = new DriveLogCategoriesTable({
			element: $("#driveLogCategoriesTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
			initialFilter: this.buildTableFilterData()
		});

		this.driveLogCategoriesDialogHandler = new DriveLogCategoriesDialogHandler();

		$("#driveLogCategoriesAddButton").click(() => {
			this.showAddDriveLogCategory();
		});

		$("#driveLogCategoriesFilterArchived").change(() => {
			this.driveLogCategoriesTable.filter(this.buildTableFilterData());
		});

		$(this.driveLogCategoriesDialogHandler)
			.on(BasicDataTable.EVENT_DBLCLICK + " " + DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_EDIT, (event, driveLogCategory: DriveLogCategory) => {
				this.driveLogCategoriesDialogHandler.setDriveLogCategory(driveLogCategory);
				this.driveLogCategoriesDialogHandler.openDialog();
			})
			.on(DriveLogCategoriesDialogHandler.EVENT_DRIVE_LOG_CATEGORY_UPDATED, () => {
				this.driveLogCategoriesTable.filter(this.buildTableFilterData());
			})
			.on(DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_ARCHIVE, (event, driveLogCategory: DriveLogCategory) => {
				this.archiveDriveLogCategory(driveLogCategory);
			})
			.on(DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_UNARCHIVE, (event, driveLogCategory: DriveLogCategory) => {
				this.unarchiveDriveLogCategory(driveLogCategory);
			})
			.on(DriveLogCategoriesTable.DRIVE_LOG_CATEGORY_DELETE, (event, driveLogCategory: DriveLogCategory) => {
				this.deleteDriveLogCategory(driveLogCategory);
			});

		$(this.driveLogCategoriesTable)
			.on(BasicDataTable.EVENT_DBLCLICK + " " + DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_EDIT, (event, driveLogCategory: DriveLogCategory) => {
				this.driveLogCategoriesDialogHandler.setDriveLogCategory(driveLogCategory);
				this.driveLogCategoriesDialogHandler.openDialog();
			})
			.on(DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_ARCHIVE, (event, driveLogCategory: DriveLogCategory) => {
				this.archiveDriveLogCategory(driveLogCategory);
			})
			.on(DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_UNARCHIVE, (event, driveLogCategory: DriveLogCategory) => {
				this.unarchiveDriveLogCategory(driveLogCategory);
			})
			.on(DriveLogCategoriesTable.DRIVE_LOG_CATEGORY_DELETE, (event, driveLogCategory: DriveLogCategory) => {
				this.deleteDriveLogCategory(driveLogCategory);
			});
	}

	private showAddDriveLogCategory() {
		if (!SecurityStatus.isAddDriveLogCategoryAllowed()) {
			BootstrapModal.info({
				title: "limit.driveLogCategory.title",
				message: "limit.driveLogCategory"
			});
		} else {
			this.driveLogCategoriesDialogHandler.resetDriveLogCategory();
			this.driveLogCategoriesDialogHandler.openDialog();
		}
	}

	private archiveDriveLogCategory(driveLogCategory: DriveLogCategory) {
		api("admin/archiveDriveLogCategory.json")
			.post({
				id: driveLogCategory.id
			})
			.done(status => {
				SecurityStatus.update(status.securityStatus);

				this.driveLogCategoriesDialogHandler.close();
				this.driveLogCategoriesTable.reload();
			});
	}

	private unarchiveDriveLogCategory(driveLogCategory: DriveLogCategory) {
		api("admin/unarchiveDriveLogCategory.json")
			.post({
				id: driveLogCategory.id
			})
			.done(status => {
				SecurityStatus.update(status.securityStatus);

				this.driveLogCategoriesDialogHandler.close();
				this.driveLogCategoriesTable.reload();
			});
	}

	private deleteDriveLogCategory(driveLogCategory: DriveLogCategory) {
		BootstrapModal.confirm({
			title: "driveLogCategory.context.delete",
			message: messages.withArguments("driveLogCategory.confirm.delete", driveLogCategory.name),
			yesLabel: "confirmation.delete",
			yesAction: (modal) => {
				modal.close();
				api("admin/deleteDriveLogCategory.json")
					.post({
						id: driveLogCategory.id
					})
					.done(status => {
						SecurityStatus.update(status.securityStatus);

						this.driveLogCategoriesDialogHandler.close();
						this.driveLogCategoriesTable.reload();
					});
			},
			noLabel: "confirmation.cancel"
		});
	}

	private buildTableFilterData() {
		return [
			{
				name: "archived",
				value: $("#driveLogCategoriesFilterArchived").val() as string
			}
		];
	}
}

class DriveLogCategoriesDialogHandler {
	public static EVENT_DRIVE_LOG_CATEGORY_DELETE = "eventDriveLogCategoryDelete";
	public static EVENT_DRIVE_LOG_CATEGORY_UPDATED = "eventDriveLogCategoryUpdated";

	private driveLogCategoryDialog: BootstrapModal.Modal;
	private observer: FieldObserver<DriveLogCategory>;
	private driveLogCategory: DriveLogCategory;

	constructor() {
		this.driveLogCategoryDialog = new BootstrapModal.Modal({
			title: "driveLogCategory.header.create",
			message: $("#driveLogCategoryDialog"),
			destroyOnClose: false,
			size: "modal-lg",
			buttons: [{
				text: "dialog.save",
				css: "saveButton btn-primary",
				action: () => { this.saveDriveLogCategory(this.driveLogCategoryDialog); }
			}, {
				text: "dialog.cancel",
				css: "cancelButton btn-default",
				action: () => { this.close(); },
			}, {
				text: "dialog.delete",
				css: "deleteButton btn-outline-danger",
				left: true,
				action: () => { $(this).triggerHandler(DriveLogCategoriesDialogHandler.EVENT_DRIVE_LOG_CATEGORY_DELETE, this.driveLogCategory); }
			}],
			onOpen: () => {
				$("#driveLogCategoryName").focus();
			},
			onClose: () => {
				FormUtil.removeAllFeedback($("#driveLogCategoryDialogForm"));
				this.removeAppendedColorTags();
			}
		});

		this.observer = new FieldObserver<DriveLogCategory>();
		this.observer.onFieldValueChange(() => {
			this.updateFieldValues();
		});
		this.initListener();

		$("#driveLogCategoryName").change(() => this.updateTitle());
		$("#driveLogCategoryDialogForm").submit(e => e.preventDefault());
	}

	public setDriveLogCategory(driveLogCategory: DriveLogCategory) {
		this.driveLogCategory = copyOfObject(driveLogCategory);
		this.observer.setModel(this.driveLogCategory);
		this.updateTitle();
		this.updateFieldValues();
		FormUtil.removeAllFeedback($("#driveLogCategoryForm"));
	}

	public openDialog() {
		this.driveLogCategoryDialog.show();
	}

	public close() {
		this.driveLogCategoryDialog.close();
	}

	private saveDriveLogCategory(modal: BootstrapModal.Modal) {
		FormUtil.removeAllFeedback($("#driveLogCategoryForm"));

		const driveLogCategoryData = serializeObjectToArray({...this.driveLogCategory});
		api("admin/saveDriveLogCategory.json")
			.post(driveLogCategoryData)
			.done(status => {
				SecurityStatus.update(status.securityStatus);

				modal.close();
				$(this).triggerHandler(DriveLogCategoriesDialogHandler.EVENT_DRIVE_LOG_CATEGORY_UPDATED);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors($("#driveLogCategoryDialogForm"), response.responseJSON.fieldErrors);
				}
			})
			.always(() => {
				Button.resetLoading(modal.getButtons("btn-primary"));
			});
	}

	public resetDriveLogCategory() {
		const firstColorElement = $(`input[name="color"]:first-child`);
		firstColorElement.prop("checked", true);
		$(`input[type="checkbox"]`).removeProp("checked");
		this.setDriveLogCategory({
			id: null,
			version: 0,
			name: "",
			business: false,
			purposeRequired: true,
			categoryId: "",
			archived: false,
			color: firstColorElement.val() as number
		});
	}

	private updateTitle() {
		let message = messages[this.driveLogCategory.id == null ? "driveLogCategory.header.create" : "driveLogCategory.header.edit"];
		if (this.driveLogCategory.name) {
			message += ": " + this.driveLogCategory.name;
		}
		this.driveLogCategoryDialog.setTitle(message, true);
	}

	private initListener() {
		this.observer.observeInput("#driveLogCategoryName", "name");
		this.observer.observeInput("#driveLogCategoryBusiness", "business", selector => $(selector).prop("checked") as boolean);
		this.observer.observeInput("#driveLogCategoryPurposeRequired", "purposeRequired", selector => $(selector).prop("checked") as boolean);
		this.observer.observeInput("#driveLogCategoryArchived", "archived", selector => $(selector).prop("checked") as boolean);
		this.observer.observeInput(`input[name="color"]`, "color", selector => Number($(selector).filter(":checked").val() as string));
	}

	private updateFieldValues() {
		const editCategoryMode = this.driveLogCategory.id != null;
		this.driveLogCategoryDialog.toggleButton("btn-outline-danger", editCategoryMode);
		$("#driveLogCategoryArchivedFormGroup").toggle(editCategoryMode);

		$("#driveLogCategoryName").val(this.driveLogCategory.name);
		$("#driveLogCategoryArchived").prop("checked", this.driveLogCategory.archived);
		$("#driveLogCategoryBusiness").prop("checked", this.driveLogCategory.business);
		$("#driveLogCategoryPurposeRequired").prop("checked", this.driveLogCategory.purposeRequired);
		if (this.driveLogCategory.color) {
			const colorTag = $(`input[name=color][value=${this.driveLogCategory.color}]`);
			// Add color when the saved drive log category contains a color that is no longer in the default selection (DriveLogCategoryColors.java)
			if (colorTag.length === 0) {
				this.appendColorTag(this.driveLogCategory.color);
			} else {
				colorTag.prop("checked", true);
			}
		}
	}

	private appendColorTag(integerColor: number) {
		const [r, g, b] = convertColorIntToHex(integerColor).match(/\w\w/g).map(x => parseInt(x, 16));
		const colorTag = `<input type="radio" name="color" id="appended-color-${integerColor}" value="${integerColor}" checked />
							  <label for="appended-color-${integerColor}">
									<span style="--drive-log-category-color: ${r}, ${g}, ${b}">
										<i class="fas fa-tag"></i>
									</span>
							  </label>`;
		$("#driveLogCategoryColor").append(colorTag);
	}

	// Removes colors that aren't in included the default selection (DriveLogCategoryColors.java)
	private removeAppendedColorTags() {
		const appendedColor = $(`[id^="appended-color"], [for^="appended-color"]`);
		if (appendedColor.length > 0) {
			appendedColor.remove();
		}
	}
}

class DriveLogCategoriesTable extends BasicDataTable<DriveLogCategory> {
	public static EVENT_DRIVE_LOG_CATEGORY_EDIT = "eventDriveLogCategoryEdit";
	public static EVENT_DRIVE_LOG_CATEGORY_ARCHIVE = "eventDriveLogCategoryArchive";
	public static EVENT_DRIVE_LOG_CATEGORY_UNARCHIVE = "eventDriveLogCategoryUnarchive";
	public static DRIVE_LOG_CATEGORY_DELETE = "eventDriveLogCategoryDelete";
	public static COLUMN_COLOR_TAG = "35px";

	constructor(config: BasicDataTableConfig) {
		super("/timr/admin/driveLogCategories.json", config);
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			rowCallback: (row, data, index) => {
				const driveLogCategory = data as DriveLogCategory;
				if (driveLogCategory.archived) {
					$(row).addClass("archived");
				}
			},
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected getColumnSettings(): TypedColumnSettings<DriveLogCategory>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{
				data: "color",
				orderable: false,
				className: "pr-5",
				render: this.renderColor,
				width: DriveLogCategoriesTable.COLUMN_COLOR_TAG
			},
			{data: "name", orderable: true, render: BasicDataTable.renderText},
			{data: "business", orderable: true, render: this.renderBusiness},
			{data: "purposeRequired", orderable: true, render: this.renderPurposeRequired},
			{
				data: "archived",
				orderable: false,
				render: this.renderArchived,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	private renderColor(this: void, data: number, type: string, row: DriveLogCategory, meta: DataTables.CellMetaSettings) {
		const [r, g, b] = convertColorIntToHex(data).match(/\w\w/g).map(x => parseInt(x, 16));
		return data ? `<div class="color-tag-box">
							<span style="--drive-log-category-color: ${r}, ${g}, ${b};">
								<i class="fas fa-tag"></i>
							</span>
						</div>` : "";
	}

	private renderArchived(this: void, data: boolean, type: string, row: DriveLogCategory, meta: DataTables.CellMetaSettings): string {
		return data ? `<i class="fal fa-archive" title="${messages["workingTimeType.archived"]}"></i>` : "";
	}

	private renderBusiness(this: void, data: boolean, type: string, row: DriveLogCategory, meta: DataTables.CellMetaSettings) {
		return data ? `<i class="fal fa-check" title="${messages["driveLogCategory.business"]}"></i>` : "";
	}

	private renderPurposeRequired(this: void, data: boolean, type: string, row: DriveLogCategory, meta: DataTables.CellMetaSettings) {
		return data ? `<i class="fal fa-check" title="${messages["driveLogCategory.purposeRequired"]}"></i>` : "";
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[2, "desc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "driveLogCategory.context.edit",
				action: driveLogCategory => { $(this).triggerHandler(DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_EDIT, driveLogCategory); }
			})
			.addMenuEntry({
				label: "driveLogCategory.context.archive",
				show: driveLogCategory => !driveLogCategory.archived,
				action: driveLogCategory => { $(this).triggerHandler(DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_ARCHIVE, driveLogCategory); }
			})
			.addMenuEntry({
				label: "driveLogCategory.context.unarchive",
				show: driveLogCategory => driveLogCategory.archived,
				action: driveLogCategory => { $(this).triggerHandler(DriveLogCategoriesTable.EVENT_DRIVE_LOG_CATEGORY_UNARCHIVE, driveLogCategory); }
			})
			.addMenuEntry({
				label: "driveLogCategory.context.delete",
				action: driveLogCategory => { $(this).triggerHandler(DriveLogCategoriesTable.DRIVE_LOG_CATEGORY_DELETE, driveLogCategory); }
			});
	}
}
